import type { RouteRecordRaw, RouterConfig } from 'vue-router'
import { storeSubdomainFrom } from '~/utils/storefrontHost'

/**
 * On a storefront host the store owns the whole site.
 *
 * The pages themselves live under `/store/[id]/*` — that is the owner-facing
 * design studio, reached from the platform host. When a visitor arrives on
 * `my-store.vntru.com` those same pages answer at the root instead,
 * so shoppers get `/products/42` rather than `/store/12/products/42`.
 *
 * Done here rather than by redirecting because the route table has to match on
 * the server and the client alike, or hydration lands on a different page than
 * the one that was rendered.
 */
export default {
  routes: (routes) => {
    if (!onStorefrontHost()) return routes

    return routes.flatMap((route) => {
      // The platform home would otherwise win `/` over the storefront's.
      if (route.path === '/') return []

      const rooted = atStorefrontRoot(route)

      return rooted ? [rooted, route] : [route]
    })
  },
} satisfies RouterConfig

function onStorefrontHost(): boolean {
  try {
    const root = String(useRuntimeConfig().public.storeRootDomain || '')
    return Boolean(storeSubdomainFrom(useRequestURL().host, root))
  }
  catch {
    if (import.meta.client && typeof location !== 'undefined') {
      return Boolean(storeSubdomainFrom(location.host, ''))
    }
    return false
  }
}

/** `/store/:id` and `/store/:id()` (unplugin-vue-router) plus nested pages. */
function storefrontPathOf(path: string): string | null {
  const match = path.match(/^\/store\/:id(?:\(\))?(?:\/(.*))?$/)
  if (!match) return null
  return match[1] ? `/${match[1]}` : '/'
}

/** The same page, answering without the `/store/{id}` prefix. */
function atStorefrontRoot(route: RouteRecordRaw): RouteRecordRaw | null {
  const path = storefrontPathOf(route.path)
  if (path == null) return null

  return {
    ...route,
    path,
    name: `storefront-root${route.name ? `-${String(route.name)}` : ''}`,
  }
}
